/* Clayit. theme. Global behaviour (deferred, vanilla, a few KB)
   - Scroll reveal for .rise (IntersectionObserver)
   - Hero pressed-mark parallax + copy lift (reduced-motion safe)
   - Mobile nav drawer toggle
   - Back/forward-cache (bfcache) revive: when a visitor opens a page and
     hits Back, the browser restores this page frozen and no inline script
     re-runs. We re-sync the UI on `pageshow` so the page comes back to life
     smoothly instead of stuck. Section scripts can register their own
     revive callback via window.__clayitRevive.push(fn).
*/
(function () {
  'use strict';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Section scripts (inline, run before this deferred file) may already have
     queued revive callbacks. Keep them; this just guarantees the array exists. */
  var revivers = (window.__clayitRevive = window.__clayitRevive || []);

  /* ---- Reveal on scroll ----
     .rise fades up. .rv is a reveal root: it stays unclipped (a clipped or
     masked child never "intersects", so it can't be observed directly) and
     CSS drives the child's clip-path / masked-line treatment off `.rv.in`. */
  function runReveals() {
    var rises = document.querySelectorAll('.rise:not(.in), .rv:not(.in)');
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
      rises.forEach(function (el) { io.observe(el); });
    } else {
      rises.forEach(function (el) { el.classList.add('in'); });
    }
  }
  runReveals();

  /* ---- Hero parallax ---- */
  var heroUpdate = null;
  (function heroParallax() {
    var mark = document.querySelector('.hero-mark');
    var inn = document.querySelector('.hero-in');
    var hero = document.querySelector('.hero');
    if (reduce || !mark || !hero) { return; }
    var ticking = false;
    heroUpdate = function update() {
      var y = window.scrollY || window.pageYOffset;
      var h = hero.offsetHeight;
      if (y <= h) {
        mark.style.setProperty('--py', (y * 0.22) + 'px');
        if (inn) {
          inn.style.transform = 'translateY(' + (y * 0.10) + 'px)';
          inn.style.opacity = Math.max(0, 1 - y / (h * 0.95));
        }
      }
      ticking = false;
    };
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(heroUpdate); ticking = true; }
    }, { passive: true });
    heroUpdate();
  })();

  /* ---- Header scroll state: quiet at the top, kiln glass once moving ---- */
  var headerUpdate = null;
  (function headerState() {
    var hdr = document.querySelector('.hdr');
    if (!hdr) { return; }
    var was = null, ticking = false;
    headerUpdate = function () {
      var s = (window.scrollY || window.pageYOffset) > 12;
      if (s !== was) { hdr.classList.toggle('is-scrolled', s); was = s; }
      ticking = false;
    };
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(headerUpdate); ticking = true; }
    }, { passive: true });
    headerUpdate();
  })();

  /* ---- Mobile nav drawer (accessible off-canvas) ---- */
  var closeDrawer = null;
  (function navDrawer() {
    var btn = document.querySelector('[data-menu-btn]');
    var drawer = document.querySelector('[data-drawer]');
    var backdrop = document.querySelector('[data-drawer-backdrop]');
    if (!btn || !drawer) { return; }
    var closeEl = drawer.querySelector('[data-drawer-close]');

    function open() {
      if (backdrop) { backdrop.hidden = false; }
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
        if (backdrop) { backdrop.classList.add('is-open'); }
      });
      drawer.setAttribute('aria-hidden', 'false');
      btn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      var first = drawer.querySelector('a, button');
      if (first) { first.focus(); }
    }
    closeDrawer = function close(focusBtn) {
      drawer.classList.remove('is-open');
      if (backdrop) { backdrop.classList.remove('is-open'); }
      drawer.setAttribute('aria-hidden', 'true');
      btn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (focusBtn !== false) { btn.focus(); }
    };
    function isOpen() { return drawer.classList.contains('is-open'); }

    btn.addEventListener('click', function () { isOpen() ? closeDrawer() : open(); });
    if (closeEl) { closeEl.addEventListener('click', function () { closeDrawer(); }); }
    if (backdrop) { backdrop.addEventListener('click', function () { closeDrawer(); }); }
    if (backdrop) {
      backdrop.addEventListener('transitionend', function (e) {
        if (e.propertyName === 'opacity' && !backdrop.classList.contains('is-open')) {
          backdrop.hidden = true;
        }
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) { closeDrawer(); }
    });
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('a')) { closeDrawer(); }
    });
  })();

  /* ---- Revive interactive sections after a bfcache restore ----
     These run generically off the DOM so the section templates don't need
     their own hooks. */
  function reviveReels() {
    /* The browser pauses cached media; the reel comes back flagged is-playing
       but frozen and its IntersectionObserver is already gone. Resume it. */
    document.querySelectorAll('[data-blb-reel].is-playing video').forEach(function (v) {
      var p = v.play(); if (p && p.catch) { p.catch(function () {}); }
    });
  }
  function revivePersonalizer() {
    /* Recompute the live cover aspect ratio so the preview frame is correct. */
    document.querySelectorAll('.blbx-stage').forEach(function (stage) {
      var cover = stage.querySelector('.cover');
      if (cover && cover.naturalWidth && cover.naturalHeight) {
        stage.style.setProperty('--cover-ar', cover.naturalWidth + '/' + cover.naturalHeight);
      }
    });
  }

  /* ---- Revive on back/forward (bfcache restore) ---- */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) { return; }             /* only when restored from bfcache */
    runReveals();                             /* catch any .rise that lost its observer */
    if (heroUpdate) { heroUpdate(); }         /* recompute parallax for the restored scroll pos */
    if (headerUpdate) { headerUpdate(); }     /* header glass state for the restored scroll pos */
    if (closeDrawer) { closeDrawer(false); }  /* never come back with a stuck-open drawer */
    document.body.style.overflow = '';
    reviveReels();
    revivePersonalizer();
    /* any section that registered its own hook still gets called */
    revivers.forEach(function (fn) { try { fn(); } catch (_) {} });
  });
})();
